import { useFlw1235EnterpriseNavCta } from '@educator/experiments/flw-1235_v2_enterprise_nav_cta'
import { useFireEvent } from '@educator/tracking/educatorTracking'
import { getEnvVar } from '@mc/client-env'
import {
  TopNavigation as TopNavigationShell,
  i18nInstance as globalNavI18n,
} from '@mc/global-navigation'
import { paths } from '@mc/paths'
import { MobileMenu } from './MobileMenu'
import classes from './TopNavigation.module.scss'

const I18N_NS = '@mc/global-navigation'

// Map of utility navigation link text to action names for tracking
const UTILITY_NAV_LINK_TEXTS = {
  'master class': 'MasterClass',
  certificates: 'Certificates',
  'on call': 'On Call',
  'at work': 'At Work',
  // flw-1235_v2_enterprise_nav_cta — the variant's copy needs its own entry or
  // `getActionName` returns null and variant clicks stop being tracked at all.
  enterprise: 'Enterprise',
} as const

/**
 * Check if an anchor element is part of the utility navigation
 * by examining its class and parent structure
 */
const isUtilityNavLink = (anchor: HTMLAnchorElement): boolean => {
  // Check if link is within a dark background nav container (mc-bg-neutral-1000)
  const navContainer = anchor.closest('[class*="mc-bg-neutral-1000"]')
  return !!navContainer
}

/**
 * Get the action name for a utility nav link based on its text content
 */
const getActionName = (linkText: string): string | null => {
  const normalizedText = linkText.toLowerCase().replace(/\s/g, '')
  const action =
    Object.entries(UTILITY_NAV_LINK_TEXTS).find(([key]) =>
      normalizedText.includes(key.replace(/\s/g, '')),
    )?.[1] ?? null
  return action
}

const handleRelativeLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
  const anchor = (e.target as HTMLElement).closest('a')
  const href = anchor?.getAttribute('href')
  const baseUrl = getEnvVar<string>('CORE_URL')
  const isRelativeLink =
    href && !href.startsWith('http') && !href.startsWith('#')

  if (anchor && isRelativeLink && baseUrl) {
    e.preventDefault()
    const absoluteUrl = `${baseUrl}${href}`

    if (anchor.target === '_blank') {
      window.open(absoluteUrl, '_blank')
    } else {
      window.location.href = absoluteUrl
    }
  }
}

interface TopNavigationProps {
  mobileBurgerWhite?: boolean
  pageSource?: string
  /** When true, the mobile hamburger is hidden (rendered by NavBar instead) */
  hideMobileMenu?: boolean
  /** Keep the utility row on screen when the page pins its own header. */
  pinned?: boolean
}

export const TopNavigation = ({
  mobileBurgerWhite,
  pageSource,
  hideMobileMenu = false,
  pinned = false,
}: TopNavigationProps) => {
  const fireEvent = useFireEvent()
  const atWorkCopy = useFlw1235EnterpriseNavCta(
    globalNavI18n.t('nav.cta.work', { ns: I18N_NS }),
  )

  /**
   * Enhanced click handler that:
   * 1. Intercepts utility navigation link clicks
   * 2. Fires educator-specific tracking event
   * 3. Prevents default tracking from shared components
   * 4. Handles relative link rewrites
   */
  const handleNavigationClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a')

    // Check if this is a utility nav link and track it with educator-specific data
    if (anchor && isUtilityNavLink(anchor)) {
      const linkText = anchor.textContent?.trim() || ''
      const action = getActionName(linkText)

      if (action) {
        // Build tracking event properties
        const trackingProps: Record<string, string> = {
          action,
          level: 'primary',
          location: 'utility-navigation',
          product: 'educator',
        }

        // Add page_source if provided
        if (pageSource) {
          trackingProps.page_source = pageSource
        }

        // Fire educator tracking event with correct page context
        fireEvent('educatorNavigationClicked', trackingProps)

        // Stop propagation to prevent shared component's tracking from firing
        e.stopPropagation()
      }
    }

    // Continue with existing relative link handling
    handleRelativeLinkClick(e)
  }

  return (
    <div
      className={`${pinned ? 'fixed z-50' : 'absolute z-2'} top-0 left-0 w-full ${classes.wrapper}`}
      onClickCapture={handleNavigationClick}
    >
      <TopNavigationShell className='d-md-block'>
        <a
          href={paths.root}
          className='mc-text-x-small mc-text--bold mc-ml-0'
          data-testid='masterclass-link'
        >
          {globalNavI18n.t('nav.cta.masterClass', { ns: I18N_NS })}
        </a>
        <a
          href={paths.certificates}
          className='mc-text-x-small mc-text--bold mc-ml-8'
        >
          {globalNavI18n.t('nav.cta.certificates', { ns: I18N_NS })}
        </a>
        <a
          href={paths.onCall}
          className='mc-text-x-small mc-text--bold mc-ml-8'
        >
          {globalNavI18n.t('nav.cta.onCall', { ns: I18N_NS })}
        </a>
        <a
          href={paths.enterpriseHomepage}
          className='mc-text-x-small mc-text--bold mc-ml-8'
        >
          {atWorkCopy}
        </a>
      </TopNavigationShell>
      {!hideMobileMenu && <MobileMenu burgerWhite={mobileBurgerWhite} />}
    </div>
  )
}
